import { useState } from "react";
import { toast } from "sonner";
import { useMutation } from "urql";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { graphql } from "@/gen";
import useDebounce from "@/hooks/useDebounce";
import { useSearchTournaments } from "@/hooks/useSearchTournaments";

const setTournamentSeriesIdMutation = graphql(`
    mutation setTournamentSeriesId($input: SetTournamentSeriesIdInput!) {
        setTournamentSeriesId(input: $input) {
            id
            tournamentSeriesId
        }
    }
`);

export function AddTournamentSeriesTournaments({ seriesId }: { seriesId: string }) {
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query, 500);

    const { tournaments, loading } = useSearchTournaments({
        query: debouncedQuery,
        pause: !debouncedQuery,
    });

    const [_, setTournamentSeriesId] = useMutation(setTournamentSeriesIdMutation);

    const addTournament = async (tournamentId: string) => {
        const result = await setTournamentSeriesId({
            input: { tournamentId, seriesId },
        });
        if (result.error) {
            toast.error("Failed to add tournament", { description: result.error.message });
        } else {
            toast.success("Tournament added to series");
            setQuery("");
        }
    };

    return (
        <div className="my-4 max-w-3xl">
            <h3 className="mb-2 text-lg font-bold">Add tournament to series</h3>
            <div className="mb-4 flex gap-2">
                <Input
                    placeholder="Search tournaments by title"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
            </div>
            {debouncedQuery !== "" && (
                <>
                    {loading && <p>Searching...</p>}
                    <ul>
                        {tournaments.map(tournament => (
                            <li
                                key={tournament.id}
                                className="flex cursor-pointer justify-between gap-2 p-2 hover:bg-gray-200"
                            >
                                <span>{tournament.title}</span>
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        if (tournament.tournamentSeriesId) {
                                            toast.error(
                                                "Tournament already in a series. Remove it from the tournament page first."
                                            );
                                            return;
                                        }
                                        addTournament(tournament.id);
                                    }}
                                >
                                    Add
                                </Button>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}
